import React, { useEffect, useState, useCallback } from 'react';
import { theme, card, input, btnPrimary, btnSecondary } from '../theme';
import { MARK_KINDS_UI, keyFromLabel } from '../utils/markKinds';

/**
 * MarkCatalogueEditor — Settings → Mark types.
 *
 * Brett, 2026-08-14: "i want those pins to be a setting so i can change them,
 * also i want some tools available in settings. So i am focused on tiling
 * inspections, 1 tool would be new tiles and be coloured red, full regrout and
 * be coloured yellow, patch regrout and be coloured green. i might do diferent
 * inspection types so need to load a different tool set."
 *
 * Pins and tools are one catalogue: a named, coloured kind of mark, differing
 * only in what it IS — a point, an area, or a run. The kind is not decoration:
 * it decides which tool appears on the plan AND the unit the totals roll up in,
 * which is what turns a drawing into "new tiles 42 m², full regrout 18 m²".
 *
 * ── THE ONE RULE THE UI HAS TO CARRY HONESTLY ────────────────────────────
 * A KEY IS PERMANENT. Names and colours are freely editable; the key is what
 * every mark ever recorded points at, so renaming it would silently rewrite the
 * meaning of work already measured and possibly quoted. Removing a type RETIRES
 * it — it stops being offered, old marks keep their meaning. The screen says so
 * plainly rather than letting someone press Remove and assume it is gone,
 * because a rule the user does not understand reads as a bug.
 */

const wrap = { ...card, padding: 18, maxWidth: 860 };
const th = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: theme.textLight, textAlign: 'left', fontWeight: 700, padding: '8px 10px' };
const td = { padding: '6px 10px', verticalAlign: 'middle' };

export default function MarkCatalogueEditor() {
  const [data, setData] = useState(null);
  const [setKey, setSetKey] = useState(null);
  const [rows, setRows] = useState([]);
  const [label, setLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    fetch('/api/mark-catalogue', { credentials: 'same-origin' })
      .then((r) => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then((j) => {
        setData(j);
        const first = (j.sets || [])[0];
        if (first) { setSetKey(first.key); setLabel(first.label); setRows(first.types || []); }
      })
      .catch((e) => setError(e.message || 'Could not load the mark types.'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const pickSet = (k) => {
    const s = (data.sets || []).find((x) => x.key === k);
    if (!s) return;
    setSetKey(s.key); setLabel(s.label); setRows(s.types || []); setMsg(null);
  };

  const patch = (i, field, value) => {
    setRows((prev) => prev.map((r, n) => (n === i ? { ...r, [field]: value } : r)));
  };

  const addRow = () => {
    setRows((prev) => prev.concat([{ key: '', label: '', color: '#3b82f6', kind: 'area', active: true, _new: true }]));
  };

  // "Remove" is retire. Said out loud on the button's own confirm, because
  // silently doing something other than what the label says is how trust goes.
  const removeRow = (i) => {
    const r = rows[i];
    if (r._new) { setRows((prev) => prev.filter((_, n) => n !== i)); return; }
    const ok = window.confirm(
      `Retire "${r.label || r.key}"?\n\n`
      + 'It stops being offered as a tool, but every mark already recorded against '
      + 'it keeps its meaning — so past measurements and quotes stay correct. '
      + 'It is not deleted.');
    if (ok) setRows((prev) => prev.filter((_, n) => n !== i));
  };

  const adoptStarter = (key) => {
    const s = (data.starters || {})[key];
    if (!s) return;
    setSetKey(key); setLabel(s.label);
    setRows(s.types.map((t, i) => ({ ...t, sortOrder: i, active: true, _new: true })));
    setMsg({ kind: 'info', text: `Loaded the ${s.label} set — press Save to keep it.` });
  };

  const save = async () => {
    setSaving(true); setError(null); setMsg(null);
    try {
      const types = rows.map((r, i) => ({
        key: (r.key || '').trim() || keyFromLabel(r.label),
        label: r.label, color: r.color, kind: r.kind,
        sortOrder: i, active: r.active !== false,
      }));
      const r = await fetch('/api/mark-catalogue', {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ setKey, label, types }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Could not save.');
      setData(j);
      const mine = (j.sets || []).find((s) => s.key === setKey);
      if (mine) setRows(mine.types || []);
      setMsg({
        kind: 'ok',
        text: j.retired
          ? `Saved. ${j.retired} type${j.retired === 1 ? '' : 's'} retired — old marks keep their meaning.`
          : 'Saved.',
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ ...wrap, textAlign: 'center', color: theme.textLight }}>Loading mark types…</div>;

  const sets = (data && data.sets) || [];
  const starters = Object.entries((data && data.starters) || {})
    .filter(([k]) => !sets.some((s) => s.key === k));

  return (
    <div style={wrap}>
      {/* 2026-08-17 — "Mark types" is what the data is called; it is not what
          anyone comes to Settings looking for. Brett went hunting for the
          markup tools and did not find this. Named for the job it does. */}
      <div style={{ fontSize: 15, fontWeight: 700, color: theme.text, marginBottom: 2 }}>Markup tools &amp; pin types</div>
      <div style={{ fontSize: 12, color: theme.textLight, marginBottom: 14, maxWidth: 640 }}>
        The tools on a plan's markup bar and the pin colours on a defect. Name them for the
        <strong> work</strong>, not the trade. A <strong>point</strong> is a pin you drop,
        an <strong>area</strong> measures m², a <strong>run</strong> measures metres — so a set totals
        itself, e.g. “retiling 42 m², full regrout 18 m”.
        {data && data.customised === false && (
          <> You are using the standard list; save any change to make it yours.</>
        )}
      </div>

      {error && (
        <div style={{ padding: 10, background: theme.bg, border: `1px solid ${theme.danger}`, color: theme.danger, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{error}</div>
      )}
      {msg && (
        <div style={{ padding: 10, background: theme.bg, border: `1px solid ${msg.kind === 'ok' ? theme.success : theme.border}`, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{msg.text}</div>
      )}

      {/* Which set */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {sets.map((s) => (
          <button key={s.key} type="button" onClick={() => pickSet(s.key)}
            style={{ ...(s.key === setKey ? btnPrimary : btnSecondary), padding: '6px 12px', fontSize: 12 }}>
            {s.label}
          </button>
        ))}
        {starters.map(([k, s]) => (
          <button key={k} type="button" onClick={() => adoptStarter(k)} title="Load this ready-made set"
            style={{ ...btnSecondary, padding: '6px 12px', fontSize: 12, borderStyle: 'dashed' }}>
            + {s.label}
          </button>
        ))}
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 11, color: theme.textLight, fontWeight: 700, display: 'block', marginBottom: 4 }}>SET NAME</label>
        <input value={label} onChange={(e) => setLabel(e.target.value)} style={{ ...input, maxWidth: 320 }} />
      </div>

      {/* A grid rather than a table: below 700px each type stacks into its own
          block (see responsive.css .mc-row). Four columns on a held iPad crush
          the name field to about six characters and clip the kind dropdown to
          "An ar⌄" — verified by rendering it, not assumed. */}
      <div className="mc-head" style={{ borderBottom: `1px solid ${theme.border}` }}>
        <div style={th}>Name</div>
        <div style={th}>What it measures</div>
        <div style={th}>Colour</div>
        <div style={th} />
      </div>

      {rows.map((r, i) => (
        <div key={r.key || `new-${i}`} className="mc-row"
          style={{ borderTop: `1px solid ${theme.border}`, opacity: r.active === false ? 0.5 : 1 }}>
          <div className="mc-cell-name" style={{ minWidth: 0 }}>
            <input value={r.label} placeholder="e.g. Full regrout"
              onChange={(e) => patch(i, 'label', e.target.value)}
              style={{ ...input, width: '100%' }} />
            {!r._new && (
              <div style={{ fontSize: 10, color: theme.textLight, marginTop: 2 }}>
                key: {r.key}{r.active === false ? ' · retired' : ''}
              </div>
            )}
          </div>
          <div className="mc-cell-kind" style={{ minWidth: 0 }}>
            <select value={r.kind} onChange={(e) => patch(i, 'kind', e.target.value)} style={{ ...input, width: '100%' }}>
              {MARK_KINDS_UI.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </div>
          <div className="mc-cell-colour">
            <input type="color" value={r.color} onChange={(e) => patch(i, 'color', e.target.value)}
              style={{ width: 46, height: 34, padding: 0, border: `1px solid ${theme.border}`, borderRadius: 6, background: 'none' }} />
          </div>
          <div className="mc-cell-retire">
            <button type="button" onClick={() => removeRow(i)}
              style={{ ...btnSecondary, padding: '7px 12px', fontSize: 12, color: theme.danger }}>
              {r._new ? 'Remove' : 'Retire'}
            </button>
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center' }}>
        <button type="button" onClick={addRow} style={{ ...btnSecondary, fontSize: 13 }}>+ Add a type</button>
        <button type="button" onClick={save} disabled={saving} style={{ ...btnPrimary, fontSize: 13, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : 'Save set'}
        </button>
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: theme.textLight, maxWidth: 640 }}>
        Names and colours can be changed whenever you like. The underlying key cannot — it is what every
        mark already on a plan points at, so changing it would alter the meaning of measurements you have
        already taken.
      </div>
    </div>
  );
}
